import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import ModeSelector from "./components/ModeSelector";
import ProfessionalMode from "./components/modes/ProfessionalMode";
import CreativeMode from "./components/modes/CreativeMode";
import CreativeRandomizer from "./components/modes/creative/CreativeRandomizer";
import AIPhilosophy from "./components/AIPhilosophy";
// import UnderConstruction from "./components/UnderConstruction";
// import SandsOfExileGame from "./components/SandsOfExile/SandsOfExileGame";
import BountifulSeaGame from "./components/BountifulSea/BountifulSeaGame";
import KotchGame from "./components/Kotch/KotchGame";
import StoryReader from "./components/StoryReader";
import StoryLibrary from "./components/StoryLibrary";
import PoemArabicWater from "./components/PoemArabicWater";
import NotFound from "./components/NotFound";
import "./styles/variables.css";
import "./styles/App.css";

// Lazy-load creative variants (only one loads per visit)
const CreativeTerminal = lazy(() => import("./components/modes/creative/CreativeTerminal"));
const CreativeConstellation = lazy(() => import("./components/modes/creative/CreativeConstellation"));
const CreativePanorama = lazy(() => import("./components/modes/creative/CreativePanorama"));
const CreativeTarot = lazy(() => import("./components/modes/creative/CreativeTarot"));
const CreativeBroadsheet = lazy(() => import("./components/modes/creative/CreativeBroadsheet"));
const CreativeRetro = lazy(() => import("./components/modes/creative/CreativeRetro"));
const CreativeChronicle = lazy(() => import("./components/modes/creative/CreativeChronicle"));
const CreativeDashboard = lazy(() => import("./components/modes/creative/CreativeDashboard"));
const CreativeJukebox = lazy(() => import("./components/modes/creative/CreativeJukebox"));
const CreativeCorkboard = lazy(() => import("./components/modes/creative/CreativeCorkboard"));
const CreativeBlueprint = lazy(() => import("./components/modes/creative/CreativeBlueprint"));
const CreativeComic = lazy(() => import("./components/modes/creative/CreativeComic"));
const CreativeVaporwave = lazy(() => import("./components/modes/creative/CreativeVaporwave"));
const CreativeMuseum = lazy(() => import("./components/modes/creative/CreativeMuseum"));
const CreativeLibrary = lazy(() => import("./components/modes/creative/CreativeLibrary"));
const CreativePixel = lazy(() => import("./components/modes/creative/CreativePixel"));
const CreativeNoir = lazy(() => import("./components/modes/creative/CreativeNoir"));
const CreativeSteampunk = lazy(() => import("./components/modes/creative/CreativeSteampunk"));
const CreativeScroll = lazy(() => import("./components/modes/creative/CreativeScroll"));
const CreativeTV = lazy(() => import("./components/modes/creative/CreativeTV"));

// Lazy-loaded so the Firebase SDK only downloads on these routes
const AliJobSearch = lazy(() => import("./components/AliJobSearch"));
const WeightTracker = lazy(() => import("./components/WeightTracker"));

const CreativeSuspense: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0a0a0a" }} />}>
    {children}
  </Suspense>
);

function App() {
  return (
    <MotionConfig reducedMotion="user">
      <Router>
        <div className="App">
          <Routes>
            <Route path="/" element={<ModeSelector />} />
            <Route path="/professional" element={<ProfessionalMode />} />
            <Route path="/creative" element={<CreativeRandomizer />} />
            <Route path="/creative/archive" element={<CreativeMode />} />
            <Route path="/creative/terminal" element={<CreativeSuspense><CreativeTerminal /></CreativeSuspense>} />
            <Route path="/creative/constellation" element={<CreativeSuspense><CreativeConstellation /></CreativeSuspense>} />
            <Route path="/creative/panorama" element={<CreativeSuspense><CreativePanorama /></CreativeSuspense>} />
            <Route path="/creative/tarot" element={<CreativeSuspense><CreativeTarot /></CreativeSuspense>} />
            <Route path="/creative/broadsheet" element={<CreativeSuspense><CreativeBroadsheet /></CreativeSuspense>} />
            <Route path="/creative/retro" element={<CreativeSuspense><CreativeRetro /></CreativeSuspense>} />
            <Route path="/creative/chronicle" element={<CreativeSuspense><CreativeChronicle /></CreativeSuspense>} />
            <Route path="/creative/dashboard" element={<CreativeSuspense><CreativeDashboard /></CreativeSuspense>} />
            <Route path="/creative/jukebox" element={<CreativeSuspense><CreativeJukebox /></CreativeSuspense>} />
            <Route path="/creative/corkboard" element={<CreativeSuspense><CreativeCorkboard /></CreativeSuspense>} />
            <Route path="/creative/blueprint" element={<CreativeSuspense><CreativeBlueprint /></CreativeSuspense>} />
            <Route path="/creative/comic" element={<CreativeSuspense><CreativeComic /></CreativeSuspense>} />
            <Route path="/creative/vaporwave" element={<CreativeSuspense><CreativeVaporwave /></CreativeSuspense>} />
            <Route path="/creative/museum" element={<CreativeSuspense><CreativeMuseum /></CreativeSuspense>} />
            <Route path="/creative/library" element={<CreativeSuspense><CreativeLibrary /></CreativeSuspense>} />
            <Route path="/creative/pixel" element={<CreativeSuspense><CreativePixel /></CreativeSuspense>} />
            <Route path="/creative/noir" element={<CreativeSuspense><CreativeNoir /></CreativeSuspense>} />
            <Route path="/creative/steampunk" element={<CreativeSuspense><CreativeSteampunk /></CreativeSuspense>} />
            <Route path="/creative/scroll" element={<CreativeSuspense><CreativeScroll /></CreativeSuspense>} />
            <Route path="/creative/tv" element={<CreativeSuspense><CreativeTV /></CreativeSuspense>} />
            <Route path="/ai-philosophy" element={<AIPhilosophy />} />
            <Route path="/ali-job-search" element={<CreativeSuspense><AliJobSearch /></CreativeSuspense>} />
            <Route path="/weight-tracker" element={<CreativeSuspense><WeightTracker /></CreativeSuspense>} />
            <Route path="/sands-of-exile" element={<div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#0a0a0a", color: "#e0e0e0", fontFamily: "system-ui, sans-serif", textAlign: "center", padding: "2rem" }}><h1 style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>Under Reconstruction</h1><p style={{ color: "#999", fontSize: "1.1rem" }}>Sands of Exile is being rebuilt. Check back soon.</p><a href="/" style={{ marginTop: "1.5rem", color: "#c8a45a", textDecoration: "none" }}>&larr; Back</a></div>} />
            <Route path="/bountiful-sea" element={<BountifulSeaGame />} />
            <Route path="/kotch" element={<KotchGame />} />
            <Route path="/story/short-stories" element={<StoryLibrary />} />
            <Route path="/story/graengale-grove" element={<StoryReader />} />
            <Route path="/poems/arabic/water" element={<PoemArabicWater />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </Router>
    </MotionConfig>
  );
}

export default App;
